import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    PenLine, Sparkles, BookOpen, Users, FileText, Download, Megaphone,
    LayoutGrid, Split, History, Maximize2, Search, Type, Check, Star,
    Rocket, ChevronRight, Quote, ArrowRight, CreditCard, Coins, Crown, Loader2, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { paymentsApi, affiliateApi } from '../lib/api';
import { EmbeddedCheckoutModal } from '../components/EmbeddedCheckout';

// Credit packages available for purchase
const CREDIT_PACKAGES = [
    { id: 'small', credits: 50, price: 6.99, description: '50 AI Credits', popular: false },
    { id: 'medium', credits: 150, price: 17.99, description: '150 AI Credits', popular: true },
    { id: 'large', credits: 500, price: 54.99, description: '500 AI Credits', popular: false },
    { id: 'mega', credits: 1500, price: 129.99, description: '1500 AI Credits', popular: false },
];

// Affiliate tracking utility
const trackAffiliateClick = async () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref');
        
        if (refCode) {
            // Store in localStorage for later signup tracking
            localStorage.setItem('eskrybe_affiliate_ref', refCode);
            
            // Get source from UTM params or referrer
            const source = urlParams.get('utm_source') || document.referrer || 'direct';
            const campaign = urlParams.get('utm_campaign') || '';
            
            // Track the click
            await affiliateApi.trackClick(refCode, source, campaign);
            console.log('Affiliate click tracked:', refCode);
        }
    } catch (error) {
        console.error('Failed to track affiliate click:', error);
    }
};

const Landing = ({ onNavigate }) => {
    const { isAuthenticated } = useAuth();
    const [showAuth, setShowAuth] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [authPlan, setAuthPlan] = useState('free'); // Plan selected when going to auth
    const [billingPeriod, setBillingPeriod] = useState('annual'); // 'annual' or 'monthly'

    // Debug: Log when onNavigate is called
    const handleFooterNav = (page) => {
        console.log('handleFooterNav called with:', page);
        console.log('onNavigate is:', typeof onNavigate);
        if (onNavigate) {
            onNavigate(page);
        } else {
            console.error('onNavigate is not defined!');
        }
    };

    // Track affiliate click on landing page load
    useEffect(() => {
        trackAffiliateClick();
    }, []);

    // Check for password reset token and show auth form
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const resetToken = urlParams.get('reset_password');
        if (resetToken) {
            setShowAuth(true);
        }
    }, []);

    // Listen for showAuthModal event (from email verification start over)
    useEffect(() => {
        const handleShowAuthModal = (event) => {
            if (event.detail?.mode === 'register') {
                setShowAuth(true);
            }
        };
        window.addEventListener('showAuthModal', handleShowAuthModal);
        return () => window.removeEventListener('showAuthModal', handleShowAuthModal);
    }, []);

    useEffect(() => {
        // Only auto-redirect if not in auth flow
        // This allows the plan selection steps to show after registration
        if (isAuthenticated && !showAuth) {
            onNavigate('dashboard');
        }
    }, [isAuthenticated, onNavigate, showAuth]);

    // Show billing page with selected plan
    if (selectedPlan) {
        return (
            <BillingPage 
                plan={selectedPlan}
                billingPeriod={billingPeriod}
                onBack={() => setSelectedPlan(null)} 
                onSignup={(plan) => {
                    setAuthPlan(plan || selectedPlan);
                    setShowAuth(true);
                    setSelectedPlan(null);
                }}
            />
        );
    }

    if (showAuth) {
        return <AuthForm 
            onBack={() => setShowAuth(false)} 
            onSuccess={() => onNavigate('dashboard')} 
            initialPlan={authPlan}
            initialBillingPeriod={billingPeriod}
        />;
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-border/50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button 
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
                        className="brand-logo cursor-pointer hover:opacity-80 transition-opacity"
                    >
                        <span className="e-prefix text-3xl">e</span>
                        <span className="wordmark text-2xl">Skrybe</span>
                    </button>
                    <nav className="hidden md:flex items-center gap-8">
                        <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
                        <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
                        <button onClick={() => handleFooterNav('academy')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">Academy</button>
                    </nav>
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" className="btn-ghost" onClick={() => setShowAuth(true)} data-testid="login-btn">
                            Log in
                        </Button>
                        <Button className="btn-primary" onClick={() => setShowAuth(true)} data-testid="get-started-btn">
                            Start Writing
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="pt-28 pb-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="brand-tagline text-sm mb-4">Create. Craft. Captivate.</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6 leading-tight">
                        Write Your Story.<br />
                        <span className="text-muted-foreground">We'll Help You Tell It.</span>
                    </h1>
                    <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                        The all-in-one writing platform with AI-powered assistance, stunning illustrations, 
                        and professional organization tools. From first draft to finished novel.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button className="btn-primary text-lg px-10 py-4" onClick={() => { setAuthPlan('free'); setShowAuth(true); }} data-testid="hero-cta">
                            <Sparkles className="mr-2 h-5 w-5" />
                            Start Free - 25 Credits Included
                        </Button>
                    </div>
                </div>

                {/* Hero Image */}
                <div className="mt-12 max-w-5xl mx-auto">
                    <div className="card-default overflow-hidden">
                        <img 
                            src="https://images.unsplash.com/photo-1763833294545-e38e4fab1961?w=1200&q=80"
                            alt="Writer's workspace"
                            className="w-full h-auto object-cover"
                        />
                    </div>
                </div>

                {/* CTA Button */}
                <div className="mt-10 text-center">
                    <Button 
                        className="bg-[#b8860b] hover:bg-[#9a7209] text-white text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
                        onClick={() => setShowAuth(true)}
                        data-testid="world-waiting-cta"
                    >
                        The World is Waiting
                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-16 px-6 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="label-text text-accent mb-3">Everything You Need</div>
                        <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-3">
                            Craft Your Masterpiece
                        </h2>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Professional writing tools, AI assistance, and beautiful illustrations—all in one place.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <FeatureCard 
                            icon={<BookOpen className="h-6 w-6" />}
                            title="Book Organization"
                            description="Chapters, scenes, and drag-drop organization. Keep your story structured and easy to navigate."
                        />
                        <FeatureCard 
                            icon={<Users className="h-6 w-6" />}
                            title="Character Profiles"
                            description="Build rich character profiles with backstories, goals, flaws, and AI-generated illustrations."
                        />
                        <FeatureCard 
                            icon={<Sparkles className="h-6 w-6" />}
                            title="AI Writing Assistant"
                            description="Get help with hooks, story arcs, and creative blocks. Powered by GPT-5.2."
                        />
                        <FeatureCard 
                            icon={<LayoutGrid className="h-6 w-6" />}
                            title="Corkboard View"
                            description="Visual index card layout for your chapters. Drag and drop to reorder your story."
                        />
                        <FeatureCard 
                            icon={<Split className="h-6 w-6" />}
                            title="Split-Screen Editor"
                            description="Write while referencing your characters, notes, or other chapters side by side."
                        />
                        <FeatureCard 
                            icon={<Maximize2 className="h-6 w-6" />}
                            title="Focus Mode"
                            description="Distraction-free writing with themes, customizable width, and keyboard shortcuts."
                        />
                        <FeatureCard 
                            icon={<History className="h-6 w-6" />}
                            title="Version Snapshots"
                            description="Save snapshots of your chapters. Restore previous versions anytime."
                        />
                        <FeatureCard 
                            icon={<Search className="h-6 w-6" />}
                            title="Find & Replace"
                            description="Search across all chapters with regex support. Replace text book-wide."
                        />
                        <FeatureCard 
                            icon={<Sparkles className="h-6 w-6" />}
                            title="AI Illustrations"
                            description="Generate consistent illustrations for your book. Standard or Premium quality."
                        />
                    </div>
                </div>
            </section>

            {/* Publish & Promote Section */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="label-text text-accent mb-3">From Draft to Bookshelf</div>
                        <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-3">
                            Publish & Promote
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Professional formatting tools and export options to get your book into readers' hands. 
                            Plus promotion resources to build your audience.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="card-default p-8 hover-lift">
                            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-6">
                                <Type className="h-7 w-7" />
                            </div>
                            <h3 className="font-serif text-xl font-semibold mb-3">Pro Book Formatting</h3>
                            <p className="text-muted-foreground mb-4">
                                Choose from 6 professional chapter heading styles or create your own. 
                                Control fonts, alignment, numbering (1, I, One), and decorative elements.
                            </p>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    Classic, Modern, Literary, Thriller, Romance, Minimal presets
                                </li>
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    Custom page breaks and section dividers
                                </li>
                            </ul>
                        </div>

                        <div className="card-default p-8 hover-lift">
                            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-6">
                                <Download className="h-7 w-7" />
                            </div>
                            <h3 className="font-serif text-xl font-semibold mb-3">Multi-Format Export</h3>
                            <p className="text-muted-foreground mb-4">
                                Export your finished manuscript in industry-standard formats ready for 
                                publishing platforms and e-readers worldwide.
                            </p>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    ePub for Kindle, Apple Books, Kobo, Nook
                                </li>
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    Print-ready PDF with proper margins
                                </li>
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    DOCX for traditional publishers
                                </li>
                            </ul>
                        </div>

                        <div className="card-default p-8 hover-lift">
                            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-6">
                                <Megaphone className="h-7 w-7" />
                            </div>
                            <h3 className="font-serif text-xl font-semibold mb-3">Book Promotion Hub</h3>
                            <p className="text-muted-foreground mb-4">
                                Launch your book with our curated list of promotion sites, review platforms, 
                                and marketing resources for indie authors.
                            </p>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    BookBub, Freebooksy, Bargain Booksy guides
                                </li>
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    ARC (Advance Reader Copy) management
                                </li>
                                <li className="flex items-center gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent" />
                                    Launch day checklist and timeline
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-16 px-6 bg-muted/30">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-10">
                        <div className="label-text text-accent mb-3">Simple Pricing</div>
                        <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-3">
                            Choose Your Plan
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Start free, upgrade when you're ready to publish.
                        </p>
                        
                        {/* Billing Toggle */}
                        <div className="flex items-center justify-center gap-3 mb-8">
                            <button 
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                    billingPeriod === 'monthly' 
                                        ? 'bg-primary text-primary-foreground' 
                                        : 'bg-muted text-muted-foreground hover:text-foreground'
                                }`}
                                onClick={() => setBillingPeriod('monthly')}
                            >
                                Monthly
                            </button>
                            <button 
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                                    billingPeriod === 'annual' 
                                        ? 'bg-primary text-primary-foreground' 
                                        : 'bg-muted text-muted-foreground hover:text-foreground'
                                }`}
                                onClick={() => setBillingPeriod('annual')}
                            >
                                Annual
                                <Badge className="bg-green-500 text-white text-xs">Save 15%</Badge>
                            </button>
                        </div>
                    </div>

                    {/* Pricing Cards - Side by Side */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Free */}
                        <div 
                            className="card-default p-5 transition-all duration-300 cursor-pointer group hover:ring-2 hover:ring-accent/50 hover:shadow-lg hover:scale-[1.02]"
                            onClick={() => setSelectedPlan('free')}
                            data-testid="pricing-free"
                        >
                            <h3 className="font-serif text-xl font-semibold mb-1">Free</h3>
                            <div className="mb-1">
                                <span className="font-serif text-3xl font-bold">$0</span>
                                <span className="text-muted-foreground text-sm">/forever</span>
                            </div>
                            <p className="text-sm text-accent font-medium mb-4">25 credits/month</p>
                            <ul className="space-y-2 mb-5 text-sm">
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    1 book project
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Basic editor & organization
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    5 AI writing assists/month
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    1 AI illustration/month
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Corkboard & Timeline views
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Export to PDF
                                </li>
                            </ul>
                            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground group-hover:bg-accent group-hover:text-white transition-colors" data-testid="launch-free">
                                <Rocket className="h-4 w-4 mr-2" />
                                Get Started
                            </Button>
                        </div>

                        {/* Writer */}
                        <div 
                            className="card-default p-5 transition-all duration-300 cursor-pointer group hover:ring-2 hover:ring-accent/50 hover:shadow-lg hover:scale-[1.02]"
                            onClick={() => setSelectedPlan('writer')}
                            data-testid="pricing-writer"
                        >
                            <h3 className="font-serif text-xl font-semibold mb-1">Writer</h3>
                            <div className="mb-1">
                                <span className="font-serif text-3xl font-bold">${billingPeriod === 'annual' ? '12' : '15'}</span>
                                <span className="text-muted-foreground text-sm">/mo</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-1">
                                {billingPeriod === 'annual' ? 'Billed annually at $144' : 'or $12/mo billed annually ($144)'}
                            </p>
                            {billingPeriod === 'annual' && (
                                <p className="text-xs text-green-600 font-medium mb-1">Save $36 per year</p>
                            )}
                            <p className="text-sm text-accent font-medium mb-4">200 credits/month</p>
                            <ul className="space-y-2 mb-5 text-sm">
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    <strong>Unlimited books</strong>
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    All editor features
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Character profiles with AI
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Plot Grid & Worldbuilding
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Version snapshots
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Find & Replace
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Split-screen & Focus mode
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Export to PDF & DOCX
                                </li>
                            </ul>
                            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground group-hover:bg-accent group-hover:text-white transition-colors" data-testid="launch-writer">
                                <Rocket className="h-4 w-4 mr-2" />
                                Start Writing
                            </Button>
                        </div>

                        {/* Author - Highlighted */}
                        <div 
                            className="card-default p-5 transition-all duration-300 cursor-pointer group ring-2 ring-accent shadow-lg hover:scale-[1.02]"
                            onClick={() => setSelectedPlan('author')}
                            data-testid="pricing-author"
                        >
                            <Badge className="bg-accent text-white mb-2">Most Popular</Badge>
                            <h3 className="font-serif text-xl font-semibold mb-1">Author</h3>
                            <div className="mb-1">
                                <span className="font-serif text-3xl font-bold">${billingPeriod === 'annual' ? '25' : '29'}</span>
                                <span className="text-muted-foreground text-sm">/mo</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-1">
                                {billingPeriod === 'annual' ? 'Billed annually at $300' : 'or $25/mo billed annually ($300)'}
                            </p>
                            {billingPeriod === 'annual' && (
                                <p className="text-xs text-green-600 font-medium mb-1">Save $48 per year</p>
                            )}
                            <p className="text-sm text-accent font-medium mb-4">600 credits/month</p>
                            <ul className="space-y-2 mb-5 text-sm">
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    <strong>Everything in Writer</strong>
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Pro Book Formatting (6 styles)
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    ePub export for e-readers
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Premium AI illustrations (GPT Image)
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Book Promotion Hub
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    AI Cover Designer
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Real-time Collaboration
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Priority AI responses
                                </li>
                            </ul>
                            <Button className="w-full bg-accent hover:bg-accent/90 text-white transition-colors" data-testid="launch-author">
                                <Rocket className="h-4 w-4 mr-2" />
                                Become an Author
                            </Button>
                        </div>

                        {/* Studio */}
                        <div 
                            className="card-default p-5 transition-all duration-300 cursor-pointer group hover:ring-2 hover:ring-accent/50 hover:shadow-lg hover:scale-[1.02]"
                            onClick={() => setSelectedPlan('studio')}
                            data-testid="pricing-studio"
                        >
                            <h3 className="font-serif text-xl font-semibold mb-1">Studio</h3>
                            <div className="mb-1">
                                <span className="font-serif text-3xl font-bold">${billingPeriod === 'annual' ? '67' : '79'}</span>
                                <span className="text-muted-foreground text-sm">/mo</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-1">
                                {billingPeriod === 'annual' ? 'Billed annually at $804' : 'or $67/mo billed annually ($804)'}
                            </p>
                            {billingPeriod === 'annual' && (
                                <p className="text-xs text-green-600 font-medium mb-1">Save $144 per year</p>
                            )}
                            <p className="text-sm text-accent font-medium mb-4">2000 credits/month</p>
                            <ul className="space-y-2 mb-5 text-sm">
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    <strong>Everything in Author</strong>
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Bulk illustration generation
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    White-label PDF export
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    API access for automation
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Priority support
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Early access to features
                                </li>
                                <li className="flex items-start gap-2 text-muted-foreground">
                                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                    Custom branding options
                                </li>
                            </ul>
                            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground group-hover:bg-accent group-hover:text-white transition-colors" data-testid="launch-studio">
                                <Rocket className="h-4 w-4 mr-2" />
                                Go Studio
                            </Button>
                        </div>
                    </div>

                    {/* Credit Purchase Note */}
                    <div className="text-center mt-8">
                        <p className="text-sm text-muted-foreground">
                            Need more credits? Purchase additional AI credit packs anytime from your dashboard.
                        </p>
                    </div>
                </div>
            </section>

            {/* Grammar Integration Info */}
            <section className="py-12 px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="label-text text-accent mb-3">Grammar & Style</div>
                    <h2 className="font-serif text-2xl font-semibold mb-3">
                        Works with Grammarly
                    </h2>
                    <p className="text-muted-foreground mb-6 text-sm">
                        Install the free Grammarly browser extension for real-time grammar, spelling, and style suggestions.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href="https://chrome.google.com/webstore/detail/grammarly" target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="btn-secondary">Chrome</Button>
                        </a>
                        <a href="https://addons.mozilla.org/firefox/addon/grammarly-1/" target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="btn-secondary">Firefox</Button>
                        </a>
                        <a href="https://microsoftedge.microsoft.com/addons/detail/grammarly" target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="btn-secondary">Edge</Button>
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 border-t border-border">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="brand-logo mb-4">
                                <span className="e-prefix text-2xl">e</span>
                                <span className="wordmark text-xl">Skrybe</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                The all-in-one writing platform for authors.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-3">Product</h4>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                                <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                                <li><button type="button" onClick={() => handleFooterNav('affiliate')} className="hover:text-foreground transition-colors text-left">Affiliate Program</button></li>
                                <li><button type="button" onClick={() => handleFooterNav('affiliate-dashboard')} className="hover:text-foreground transition-colors text-left">Affiliate Login</button></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-3">Support</h4>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li><button type="button" onClick={() => handleFooterNav('contact')} className="hover:text-foreground transition-colors text-left">Contact Us</button></li>
                                <li><button type="button" onClick={() => handleFooterNav('academy')} className="hover:text-foreground transition-colors text-left">Academy</button></li>
                                <li><button type="button" onClick={() => handleFooterNav('admin')} className="hover:text-foreground transition-colors text-left">Admin</button></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-3">Legal</h4>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li><button type="button" onClick={() => handleFooterNav('privacy')} className="hover:text-foreground transition-colors text-left" data-testid="footer-privacy-link">Privacy Policy</button></li>
                                <li><button type="button" onClick={() => handleFooterNav('terms')} className="hover:text-foreground transition-colors text-left" data-testid="footer-terms-link">Terms of Use</button></li>
                                <li><button type="button" onClick={() => handleFooterNav('cookies')} className="hover:text-foreground transition-colors text-left" data-testid="footer-cookies-link">Cookie Notice</button></li>
                            </ul>
                        </div>
                    </div>
                    <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-muted-foreground">
                            © {new Date().getFullYear()} eSkrybe, LLC. All rights reserved.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Create. Craft. Captivate.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

// Plan details for the billing page - pricing varies by billing period
const PLAN_DETAILS = {
    free: {
        name: 'Free',
        monthlyPrice: 0,
        annualPrice: 0,
        credits: '25 credits/month',
        description: 'Perfect for exploring eSkrybe and starting your first project.',
        benefits: [
            { title: 'Book Organization', description: '1 book project with chapters, scenes, and drag-drop reordering' },
            { title: 'Basic Editor', description: 'Rich text editor with formatting tools and auto-save' },
            { title: 'AI Writing Assistant', description: '5 AI assists per month for hooks, dialogue, and scene descriptions' },
            { title: 'AI Illustration', description: '1 standard quality illustration per month' },
            { title: 'Visual Planning', description: 'Corkboard view and Timeline for story structure' },
            { title: 'PDF Export', description: 'Export your manuscript to PDF format' }
        ]
    },
    writer: {
        name: 'Writer',
        monthlyPrice: 15,
        annualPrice: 144,
        annualPerMonth: 12,
        credits: '200 credits/month',
        description: 'For serious writers ready to organize and develop their stories professionally.',
        benefits: [
            { title: 'Unlimited Books', description: 'Create as many book projects as you need' },
            { title: 'Full Editor Suite', description: 'Split-screen editing, Focus mode, Find & Replace across all chapters' },
            { title: 'Character Development', description: 'Rich character profiles with AI-powered backstory and arc suggestions' },
            { title: 'Plot Grid & Worldbuilding', description: 'Visual plot planning and comprehensive worldbuilding tools' },
            { title: 'Version Control', description: 'Save snapshots of chapters and restore previous versions anytime' },
            { title: 'Multi-Format Export', description: 'Export to PDF and DOCX formats' }
        ]
    },
    author: {
        name: 'Author',
        monthlyPrice: 29,
        annualPrice: 300,
        annualPerMonth: 25,
        credits: '600 credits/month',
        description: 'For authors ready to publish and promote their work professionally.',
        benefits: [
            { title: 'Everything in Writer', description: 'All Writer features included' },
            { title: 'Pro Book Formatting', description: '6 professional chapter heading presets with full customization' },
            { title: 'ePub Export', description: 'Export to ePub for Kindle, Apple Books, Kobo, and other e-readers' },
            { title: 'Premium AI Illustrations', description: 'Higher quality illustrations powered by GPT Image 1' },
            { title: 'Book Promotion Hub', description: 'Curated promotion sites, ARC management, and launch planning tools' },
            { title: 'Priority AI', description: 'Faster AI responses and priority processing' }
        ]
    },
    studio: {
        name: 'Studio',
        monthlyPrice: 79,
        annualPrice: 804,
        annualPerMonth: 67,
        credits: '2000 credits/month',
        description: 'For prolific authors and writing teams who need maximum power and flexibility.',
        benefits: [
            { title: 'Everything in Author', description: 'All Author features included' },
            { title: 'Bulk Illustrations', description: 'Generate multiple illustrations in batches for entire books' },
            { title: 'White-Label Export', description: 'Custom branding options for your exported manuscripts' },
            { title: 'API Access', description: 'Programmatic access for automation and integrations' },
            { title: 'Priority Support', description: 'Direct support channel with faster response times' },
            { title: 'Early Access', description: 'Be the first to try new features before general release' }
        ]
    }
};

// Helper function to format price for display
const formatPlanPrice = (planId, billingPeriod) => {
    const plan = PLAN_DETAILS[planId];
    if (!plan) return { price: '$0', period: '/forever' };
    if (planId === 'free') return { price: '$0', period: '/forever' };
    
    // Calculate savings: (monthly * 12) - annual
    const savings = (plan.monthlyPrice * 12) - plan.annualPrice;
    
    if (billingPeriod === 'annual') {
        return { 
            price: `$${plan.annualPerMonth}`, 
            period: '/mo',
            billedAs: `Billed annually at $${plan.annualPrice}`,
            savings: savings > 0 ? `Save $${savings} per year` : null
        };
    } else {
        return { 
            price: `$${plan.monthlyPrice}`, 
            period: '/mo',
            billedAs: null,
            savings: null
        };
    }
};

// Testimonials data
const TESTIMONIALS = [
    {
        quote: "eSkrybe transformed my writing process. The AI assistant helped me break through writer's block, and the corkboard view makes organizing my novel a breeze.",
        author: "Sarah Mitchell",
        role: "Romance Author, 12 published novels"
    },
    {
        quote: "Finally, a writing tool that understands what authors need. The character profiles and plot grid alone are worth the subscription.",
        author: "James Chen",
        role: "Thriller Writer"
    },
    {
        quote: "I've tried Scrivener, Dabble, and others. eSkrybe's AI features and export options make it the complete package for indie authors.",
        author: "Elena Rodriguez",
        role: "Self-Published Fantasy Author"
    },
    {
        quote: "The illustration feature is game-changing. I can visualize my characters exactly as I imagine them and keep consistency throughout my series.",
        author: "Marcus Webb",
        role: "Sci-Fi Author"
    },
    {
        quote: "As a writing coach, I recommend eSkrybe to all my clients. The organization tools help new writers structure their stories properly from day one.",
        author: "Dr. Patricia Hayes",
        role: "Writing Coach & Editor"
    },
    {
        quote: "The Focus Mode is perfect for deep writing sessions. I've increased my daily word count by 40% since switching to eSkrybe.",
        author: "David Park",
        role: "Literary Fiction Author"
    }
];

// Billing Page Component
const BillingPage = ({ plan, billingPeriod = 'annual', onBack, onSignup }) => {
    const planDetails = PLAN_DETAILS[plan];
    const priceInfo = formatPlanPrice(plan, billingPeriod);
    
    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                        <ChevronRight className="h-4 w-4 rotate-180" />
                        Back to Plans
                    </button>
                    <div className="brand-logo">
                        <span className="e-prefix text-3xl">e</span>
                        <span className="wordmark text-2xl">Skrybe</span>
                    </div>
                    <div className="w-24" /> {/* Spacer for centering */}
                </div>
            </header>

            {/* Plan Hero */}
            <section className="py-16 px-6 bg-muted/30">
                <div className="max-w-4xl mx-auto text-center">
                    <Badge className="bg-accent/10 text-accent mb-4">{planDetails.name} Plan</Badge>
                    <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">
                        {priceInfo.price}<span className="text-2xl text-muted-foreground">{priceInfo.period}</span>
                    </h1>
                    {priceInfo.billedAs && (
                        <p className="text-sm text-muted-foreground mb-1">{priceInfo.billedAs}</p>
                    )}
                    {priceInfo.savings && (
                        <p className="text-sm text-green-600 font-medium mb-2">{priceInfo.savings}</p>
                    )}
                    <p className="text-lg text-muted-foreground mb-2">{planDetails.credits}</p>
                    <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                        {planDetails.description}
                    </p>
                    <Button 
                        className="bg-accent hover:bg-accent/90 text-white text-lg px-12 py-6 rounded-full shadow-lg"
                        onClick={() => onSignup(plan)}
                        data-testid="billing-signup"
                    >
                        <Rocket className="h-5 w-5 mr-2" />
                        Get Started with {planDetails.name}
                    </Button>
                </div>
            </section>

            {/* Benefits Grid */}
            <section className="py-16 px-6">
                <div className="max-w-5xl mx-auto">
                    <h2 className="font-serif text-2xl font-semibold text-center mb-12">
                        What's Included
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {planDetails.benefits.map((benefit, i) => (
                            <div key={i} className="p-6 rounded-xl border border-border/50 bg-card">
                                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-4">
                                    <Check className="h-5 w-5" />
                                </div>
                                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                                <p className="text-sm text-muted-foreground">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 px-6 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <h2 className="font-serif text-2xl font-semibold text-center mb-4">
                        Writers Love eSkrybe
                    </h2>
                    <p className="text-center text-muted-foreground mb-12">
                        Join thousands of authors crafting their stories with us
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((testimonial, i) => (
                            <div key={i} className="p-6 rounded-xl bg-card border border-border/50">
                                <Quote className="h-8 w-8 text-accent/30 mb-4" />
                                <p className="text-sm text-muted-foreground mb-4 italic">
                                    "{testimonial.quote}"
                                </p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                                        <Star className="h-5 w-5 text-accent" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">{testimonial.author}</p>
                                        <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16 px-6">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="font-serif text-3xl font-semibold mb-4">
                        Ready to Write Your Story?
                    </h2>
                    <p className="text-muted-foreground mb-8">
                        Join eSkrybe today and start crafting your masterpiece with the tools you deserve.
                    </p>
                    <Button 
                        className="bg-[#b8860b] hover:bg-[#9a7209] text-white text-lg px-10 py-6 rounded-full shadow-lg"
                        onClick={() => onSignup(plan)}
                    >
                        <Rocket className="h-5 w-5 mr-2" />
                        Start Your Journey
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-border">
                <div className="max-w-6xl mx-auto text-center">
                    <p className="text-sm text-muted-foreground">
                        © 2024 eSkrybe. Create. Craft. Captivate.
                    </p>
                </div>
            </footer>
        </div>
    );
};

const FeatureCard = ({ icon, title, description }) => (
    <div className="card-default p-6 hover-lift">
        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-4">
            {icon}
        </div>
        <h3 className="font-serif text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
    </div>
);

const PricingCard = ({ name, price, period, credits, features, highlighted, onSelect }) => (
    <div 
        className={`card-default p-6 transition-all duration-300 cursor-pointer group
            ${highlighted 
                ? 'ring-2 ring-accent shadow-lg' 
                : 'hover:ring-2 hover:ring-accent/50 hover:shadow-lg'
            }
            hover:scale-[1.02] hover:-translate-y-1`}
        onClick={onSelect}
        data-testid={`pricing-${name.toLowerCase()}`}
    >
        {highlighted && (
            <Badge className="bg-accent text-white mb-3">Most Popular</Badge>
        )}
        <h3 className="font-serif text-xl font-semibold mb-1">{name}</h3>
        <div className="flex items-baseline gap-1 mb-2">
            <span className="font-serif text-3xl font-bold">{price}</span>
            <span className="text-muted-foreground text-sm">{period}</span>
        </div>
        <p className="text-sm text-accent font-medium mb-4">{credits}</p>
        <ul className="space-y-2 mb-6">
            {features.map((feature, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <Check className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                    {feature}
                </li>
            ))}
        </ul>
        <Button 
            className={`w-full ${highlighted 
                ? 'bg-accent hover:bg-accent/90 text-white' 
                : 'bg-primary hover:bg-primary/90 text-primary-foreground group-hover:bg-accent group-hover:text-white'
            } transition-colors`}
            onClick={(e) => {
                e.stopPropagation();
                onSelect();
            }}
            data-testid={`launch-${name.toLowerCase()}`}
        >
            <Rocket className="h-4 w-4 mr-2" />
            Launch
        </Button>
    </div>
);

const AuthForm = ({ onBack, onSuccess, initialPlan = 'free', initialBillingPeriod = 'annual' }) => {
    const { login, register } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [step, setStep] = useState('auth'); // 'auth', 'plan', 'credits', 'forgot-password', 'reset-password'
    const [selectedPlan, setSelectedPlan] = useState(initialPlan);
    const [selectedCredits, setSelectedCredits] = useState(null);
    const [billingPeriod, setBillingPeriod] = useState(initialBillingPeriod);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
    });
    const [resetEmail, setResetEmail] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    // Embedded checkout state
    const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false);
    const [embeddedCheckoutType, setEmbeddedCheckoutType] = useState(null);
    const [embeddedPlanId, setEmbeddedPlanId] = useState(null);
    const [embeddedPackageId, setEmbeddedPackageId] = useState(null);
    const [embeddedBillingPeriod, setEmbeddedBillingPeriod] = useState(initialBillingPeriod);

    // Promo code state
    const [promoCode, setPromoCode] = useState('');
    const [promoValidating, setPromoValidating] = useState(false);
    const [promoApplied, setPromoApplied] = useState(null); // Validated promo details
    const [promoError, setPromoError] = useState('');

    // Check for reset token in URL on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('reset_password');
        if (token) {
            setResetToken(token);
            setStep('reset-password');
            // Clear the URL parameter
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            await fetch(`${API_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });
            toast.success('If an account exists with this email, you will receive a password reset link.');
            setStep('auth');
            setResetEmail('');
        } catch (error) {
            toast.error('Failed to send reset email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setIsLoading(true);
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: resetToken, new_password: newPassword })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to reset password');
            }
            toast.success('Password reset successfully! You can now log in.');
            setStep('auth');
            setNewPassword('');
            setConfirmPassword('');
            setResetToken('');
        } catch (error) {
            toast.error(error.message || 'Failed to reset password. The link may have expired.');
        } finally {
            setIsLoading(false);
        }
    };

    // Promo code functions
    const handleValidatePromo = async () => {
        if (!promoCode.trim()) return;
        setPromoValidating(true);
        setPromoError('');
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/promo/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: promoCode.trim() })
            });
            const data = await response.json();
            if (response.ok && data.valid) {
                setPromoApplied(data);
                setPromoError('');
                toast.success(`Promo code valid! ${data.description}`);
            } else {
                setPromoApplied(null);
                setPromoError(data.detail || 'Invalid promo code');
            }
        } catch (error) {
            setPromoApplied(null);
            setPromoError('Failed to validate promo code');
        } finally {
            setPromoValidating(false);
        }
    };

    const handleApplyPromo = async () => {
        if (!promoApplied) return;
        setIsLoading(true);
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const token = localStorage.getItem('eskrybe_token');
            const response = await fetch(`${API_URL}/api/promo/apply`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ code: promoCode.trim() })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                toast.success(data.message);
                onSuccess(); // Refresh user data and proceed
            } else {
                toast.error(data.detail || 'Failed to apply promo code');
            }
        } catch (error) {
            toast.error('Failed to apply promo code');
        } finally {
            setIsLoading(false);
        }
    };

    const clearPromo = () => {
        setPromoCode('');
        setPromoApplied(null);
        setPromoError('');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await login(formData.email, formData.password);
            toast.success('Welcome back!');
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await register(formData.email, formData.password, formData.name);
            toast.success('Account created! Please check your email to verify your account.');
            // Always call onSuccess after registration - App.js will handle email verification
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
            setIsLoading(false);
        }
    };

    const handlePlanCheckout = async (plan, period = billingPeriod) => {
        if (plan === 'free') {
            toast.success('Welcome to eSkrybe! You have 25 free credits.');
            onSuccess();
            return;
        }

        // Use embedded checkout
        setEmbeddedCheckoutType('subscription');
        setEmbeddedPlanId(plan);
        setEmbeddedPackageId(null);
        setEmbeddedBillingPeriod(period);
        setShowEmbeddedCheckout(true);
    };

    const handleCreditPurchase = async (pkg) => {
        if (!pkg) {
            onSuccess();
            return;
        }

        // Use embedded checkout
        setEmbeddedCheckoutType('credits');
        setEmbeddedPlanId(null);
        setEmbeddedPackageId(pkg.id);
        setShowEmbeddedCheckout(true);
    };
    
    const handleEmbeddedCheckoutSuccess = () => {
        toast.success('Payment successful! Welcome to eSkrybe!');
        setShowEmbeddedCheckout(false);
        onSuccess();
    };

    // Plan selection step
    if (step === 'plan') {
        // Pre-select author plan as the recommended plan when showing upgrade options
        const displayPlan = selectedPlan === 'free' ? 'author' : selectedPlan;
        
        // Pricing based on billing period
        const planPricing = {
            writer: { monthly: 15, annual: 12, annualTotal: 144 },
            author: { monthly: 29, annual: 25, annualTotal: 300 },
            studio: { monthly: 79, annual: 67, annualTotal: 804 }
        };
        
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-3xl">
                    <CardHeader className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                            <Crown className="h-8 w-8 text-accent" />
                        </div>
                        <CardTitle className="font-serif text-2xl">Unlock Your Full Potential</CardTitle>
                        <CardDescription>Upgrade to access premium features and more credits</CardDescription>
                        
                        {/* Billing Toggle */}
                        <div className="flex items-center justify-center gap-3 mt-4">
                            <button 
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                    billingPeriod === 'monthly' 
                                        ? 'bg-primary text-primary-foreground' 
                                        : 'bg-muted text-muted-foreground hover:text-foreground'
                                }`}
                                onClick={() => setBillingPeriod('monthly')}
                            >
                                Monthly
                            </button>
                            <button 
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                                    billingPeriod === 'annual' 
                                        ? 'bg-primary text-primary-foreground' 
                                        : 'bg-muted text-muted-foreground hover:text-foreground'
                                }`}
                                onClick={() => setBillingPeriod('annual')}
                            >
                                Annual
                                <Badge className="bg-green-500 text-white text-xs">Save 15%</Badge>
                            </button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                            {/* Writer Plan */}
                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                    displayPlan === 'writer' 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedPlan('writer')}
                            >
                                <h4 className="font-semibold mb-1">Writer</h4>
                                <p className="text-2xl font-bold">
                                    ${billingPeriod === 'annual' ? planPricing.writer.annual : planPricing.writer.monthly}
                                    <span className="text-sm text-muted-foreground">/mo</span>
                                </p>
                                {billingPeriod === 'annual' && (
                                    <p className="text-xs text-muted-foreground">Billed annually at ${planPricing.writer.annualTotal}</p>
                                )}
                                <p className="text-sm text-accent mb-2">200 credits/month</p>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> Unlimited books</li>
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> All editor features</li>
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> PDF & DOCX export</li>
                                </ul>
                            </div>

                            {/* Author Plan */}
                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                                    displayPlan === 'author' 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedPlan('author')}
                            >
                                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-accent text-white text-xs">Most Popular</Badge>
                                <h4 className="font-semibold mb-1 mt-2">Author</h4>
                                <p className="text-2xl font-bold">
                                    ${billingPeriod === 'annual' ? planPricing.author.annual : planPricing.author.monthly}
                                    <span className="text-sm text-muted-foreground">/mo</span>
                                </p>
                                {billingPeriod === 'annual' && (
                                    <p className="text-xs text-muted-foreground">Billed annually at ${planPricing.author.annualTotal}</p>
                                )}
                                <p className="text-sm text-accent mb-2">600 credits/month</p>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> Everything in Writer</li>
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> ePub export</li>
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> Premium illustrations</li>
                                </ul>
                            </div>

                            {/* Studio Plan */}
                            <div 
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                    displayPlan === 'studio' 
                                        ? 'border-accent bg-accent/5' 
                                        : 'border-border hover:border-accent/50'
                                }`}
                                onClick={() => setSelectedPlan('studio')}
                            >
                                <h4 className="font-semibold mb-1">Studio</h4>
                                <p className="text-2xl font-bold">
                                    ${billingPeriod === 'annual' ? planPricing.studio.annual : planPricing.studio.monthly}
                                    <span className="text-sm text-muted-foreground">/mo</span>
                                </p>
                                {billingPeriod === 'annual' && (
                                    <p className="text-xs text-muted-foreground">Billed annually at ${planPricing.studio.annualTotal}</p>
                                )}
                                <p className="text-sm text-accent mb-2">2000 credits/month</p>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> Everything in Author</li>
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> Bulk illustrations</li>
                                    <li className="flex items-center gap-1"><Check className="h-3 w-3 text-accent" /> API access</li>
                                </ul>
                            </div>
                        </div>

                        {/* Promo Code Section */}
                        <div className="mb-6 p-4 bg-muted/30 rounded-lg border">
                            <p className="text-sm font-medium mb-2">Have a promo code?</p>
                            {promoApplied ? (
                                <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                                    <div>
                                        <p className="font-medium text-green-700">{promoApplied.code}</p>
                                        <p className="text-xs text-green-600">{promoApplied.description}</p>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={clearPromo} className="text-red-500">
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Enter promo code"
                                        value={promoCode}
                                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                        className="flex-1"
                                        data-testid="promo-code-input"
                                    />
                                    <Button 
                                        variant="outline" 
                                        onClick={handleValidatePromo}
                                        disabled={promoValidating || !promoCode.trim()}
                                        data-testid="apply-promo-btn"
                                    >
                                        {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                                    </Button>
                                </div>
                            )}
                            {promoError && <p className="text-xs text-red-500 mt-1">{promoError}</p>}
                        </div>

                        <div className="flex gap-3">
                            {promoApplied ? (
                                <Button 
                                    className="w-full bg-green-600 hover:bg-green-700"
                                    onClick={handleApplyPromo}
                                    disabled={isLoading}
                                    data-testid="activate-promo-btn"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Activating...
                                        </>
                                    ) : (
                                        <>
                                            <Gift className="h-4 w-4 mr-2" />
                                            Activate {promoApplied.plan.charAt(0).toUpperCase() + promoApplied.plan.slice(1)} Plan
                                        </>
                                    )}
                                </Button>
                            ) : (
                                <>
                                    <Button 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => {
                                            toast.success('Welcome to eSkrybe! You have 25 free credits.');
                                            onSuccess();
                                        }}
                                        disabled={checkoutLoading}
                                    >
                                        Continue with Free
                                    </Button>
                                    <Button 
                                        className="flex-1 bg-accent hover:bg-accent/90"
                                        onClick={() => handlePlanCheckout(displayPlan, billingPeriod)}
                                        disabled={checkoutLoading}
                                    >
                                        {checkoutLoading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Redirecting...
                                            </>
                                        ) : (
                                            <>
                                                <CreditCard className="h-4 w-4 mr-2" />
                                                Upgrade to {PLAN_DETAILS[displayPlan]?.name}
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Credit purchase step
    if (step === 'credits') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-2xl">
                    <CardHeader className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                            <Coins className="h-8 w-8 text-accent" />
                        </div>
                        <CardTitle className="font-serif text-2xl">Need More Credits?</CardTitle>
                        <CardDescription>Purchase additional AI credits anytime</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            {CREDIT_PACKAGES.map((pkg) => (
                                <div 
                                    key={pkg.id}
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-center relative ${
                                        selectedCredits?.id === pkg.id 
                                            ? 'border-accent bg-accent/5' 
                                            : 'border-border hover:border-accent/50'
                                    }`}
                                    onClick={() => setSelectedCredits(pkg)}
                                >
                                    {pkg.popular && (
                                        <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-accent text-white text-xs">Best Value</Badge>
                                    )}
                                    <p className="text-2xl font-bold mt-1">{pkg.credits}</p>
                                    <p className="text-xs text-muted-foreground mb-2">credits</p>
                                    <p className="font-semibold text-accent">${pkg.price}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-3">
                            <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={() => handleCreditPurchase(null)}
                                disabled={checkoutLoading}
                            >
                                Skip for Now
                            </Button>
                            <Button 
                                className="flex-1 bg-accent hover:bg-accent/90"
                                onClick={() => handleCreditPurchase(selectedCredits)}
                                disabled={!selectedCredits || checkoutLoading}
                            >
                                {checkoutLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Redirecting...
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="h-4 w-4 mr-2" />
                                        Buy {selectedCredits?.credits || 0} Credits
                                    </>
                                )}
                            </Button>
                        </div>

                        <p className="text-xs text-muted-foreground text-center mt-4">
                            You can always purchase more credits later from your dashboard.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Forgot Password step
    if (step === 'forgot-password') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="brand-logo justify-center mb-4">
                            <span className="e-prefix text-3xl">e</span>
                            <span className="wordmark text-2xl">Skrybe</span>
                        </div>
                        <CardTitle className="font-serif text-2xl">Reset Your Password</CardTitle>
                        <CardDescription>
                            Enter your email address and we'll send you a link to reset your password.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleForgotPassword} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="reset-email">Email</Label>
                                <Input
                                    id="reset-email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    required
                                    data-testid="reset-email"
                                />
                            </div>
                            <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="send-reset-link">
                                {isLoading ? 'Sending...' : 'Send Reset Link'}
                            </Button>
                        </form>
                        <div className="mt-6 text-center">
                            <Button variant="ghost" onClick={() => setStep('auth')} className="text-sm text-muted-foreground">
                                ← Back to login
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Reset Password step (when user clicks link from email)
    if (step === 'reset-password') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="brand-logo justify-center mb-4">
                            <span className="e-prefix text-3xl">e</span>
                            <span className="wordmark text-2xl">Skrybe</span>
                        </div>
                        <CardTitle className="font-serif text-2xl">Create New Password</CardTitle>
                        <CardDescription>
                            Enter your new password below.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-password">New Password</Label>
                                <Input
                                    id="new-password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="new-password"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">Confirm Password</Label>
                                <Input
                                    id="confirm-password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="confirm-password"
                                />
                            </div>
                            <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="reset-password-submit">
                                {isLoading ? 'Resetting...' : 'Reset Password'}
                            </Button>
                        </form>
                        <div className="mt-6 text-center">
                            <Button variant="ghost" onClick={() => { setStep('auth'); setResetToken(''); }} className="text-sm text-muted-foreground">
                                ← Back to login
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Auth step (login/register)
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="brand-logo justify-center mb-4">
                        <span className="e-prefix text-3xl">e</span>
                        <span className="wordmark text-2xl">Skrybe</span>
                    </div>
                    <CardTitle className="font-serif text-2xl">Welcome to eSkrybe</CardTitle>
                    <CardDescription>
                        {initialPlan !== 'free' 
                            ? `Start with ${PLAN_DETAILS[initialPlan]?.name} - ${PLAN_DETAILS[initialPlan]?.credits}`
                            : 'Start writing your masterpiece'
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Show selected plan badge */}
                    {initialPlan !== 'free' && (
                        <div className="mb-4 p-3 rounded-lg bg-accent/10 flex items-center justify-between">
                            <div>
                                <p className="font-medium text-sm">{PLAN_DETAILS[initialPlan]?.name} Plan</p>
                                <p className="text-xs text-muted-foreground">{PLAN_DETAILS[initialPlan]?.price}{PLAN_DETAILS[initialPlan]?.period}</p>
                            </div>
                            <Badge variant="outline" className="text-accent border-accent">Selected</Badge>
                        </div>
                    )}

                    <Tabs defaultValue="register">
                        <TabsList className="grid w-full grid-cols-2 mb-6">
                            <TabsTrigger value="login" data-testid="login-tab">Log In</TabsTrigger>
                            <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="login">
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="login-email">Email</Label>
                                    <Input
                                        id="login-email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        data-testid="login-email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="login-password">Password</Label>
                                        <button
                                            type="button"
                                            onClick={() => setStep('forgot-password')}
                                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                            data-testid="forgot-password-link"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                    <Input
                                        id="login-password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        data-testid="login-password"
                                    />
                                </div>
                                <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="login-submit">
                                    {isLoading ? 'Logging in...' : 'Log In'}
                                </Button>
                            </form>
                        </TabsContent>
                        
                        <TabsContent value="register">
                            <form onSubmit={handleRegister} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="register-name">Name</Label>
                                    <Input
                                        id="register-name"
                                        type="text"
                                        placeholder="Your name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="register-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="register-email">Email</Label>
                                    <Input
                                        id="register-email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        data-testid="register-email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="register-password">Password</Label>
                                    <Input
                                        id="register-password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        data-testid="register-password"
                                    />
                                </div>
                                <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="register-submit">
                                    {isLoading ? 'Creating account...' : initialPlan !== 'free' ? `Create Account & Continue` : 'Create Account'}
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                    
                    <div className="mt-6 text-center">
                        <Button variant="ghost" onClick={onBack} className="text-sm text-muted-foreground">
                            ← Back to home
                        </Button>
                    </div>
                </CardContent>
            </Card>
            
            {/* Embedded Checkout Modal */}
            <EmbeddedCheckoutModal
                isOpen={showEmbeddedCheckout}
                onClose={() => setShowEmbeddedCheckout(false)}
                checkoutType={embeddedCheckoutType}
                planId={embeddedPlanId}
                packageId={embeddedPackageId}
                billingPeriod={embeddedBillingPeriod}
                onSuccess={handleEmbeddedCheckoutSuccess}
            />
        </div>
    );
};

export default Landing;
